import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../components/Modal';
import { useAppContext } from '../../../contexts/AppContext';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { formatBRL } from '../../agenda/cliente/List/helpers';
import { normalizeSearch } from '../../recorrencias/helpers';
import { useServicosContext } from '../Context';
import ServicosOrderConfig from '../Order';
import { ServicosService } from '../Service';
import { formatDuration } from './Duration';
import { ChevronRight, Scissors, Search, TriangleAlert } from 'lucide-react';

type StatusFilter = 'active' | 'inactive';

const STATUS_ACTIVE = 1;
const STATUS_INACTIVE = 2;
const service = new ServicosService();

export default function ServicosList() {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { data, loading, error, reloadData } = useServicosContext();
    const { action, clear, setParams } = useParamsContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [togglingId, setTogglingId] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
    const [q, setQ] = useState('');

    const searched = useMemo(() => {
        const term = normalizeSearch(q);
        const ordered = [...data].sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
        if (!term) return ordered;

        return ordered.filter((s) => normalizeSearch(`${s?.name ?? ''} ${s?.description ?? ''}`).includes(term));
    }, [data, q]);

    const activeCount = searched.filter((s) => s.status === STATUS_ACTIVE).length;
    const inactiveCount = searched.filter((s) => s.status === STATUS_INACTIVE).length;
    const lista = searched.filter((s) => s.status === (statusFilter === 'active' ? STATUS_ACTIVE : STATUS_INACTIVE));

    useEffect(() => {
        setIsModalOpen(action === 'order');
    }, [action]);

    const handleCloseOrderModal = () => {
        setIsModalOpen(false);
        if (action === 'order') clear();
    };

    const toggleStatus = async (row: any) => {
        const nextStatus = row.status === STATUS_ACTIVE ? STATUS_INACTIVE : STATUS_ACTIVE;

        try {
            setTogglingId(row._id);
            await service.update(row._id, { status: nextStatus });
            await reloadData();
            successSnackbar(nextStatus === STATUS_ACTIVE ? 'Serviço ativado.' : 'Serviço desativado.');
        } catch (e) {
            console.log(e);
            errorSnackbar(`Não foi possível ${nextStatus === STATUS_ACTIVE ? 'ativar' : 'desativar'} o serviço.`);
        } finally {
            setTogglingId('');
        }
    };

    // Recarregar depois de uma acao nao pode desmontar a lista nem o modal de ordenacao.
    if (loading && !data.length) {
        return (
            <div className="space-y-2 px-2" aria-busy="true">
                <div className="sf-skeleton h-10" />
                {[0, 1, 2, 3].map((item) => (
                    <div key={item} className="sf-skeleton h-16" />
                ))}
            </div>
        );
    }

    if (error && !data.length) {
        return (
            <div className="sf-state">
                <TriangleAlert aria-hidden />
                <h2>Não foi possível carregar os serviços</h2>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void reloadData()}>
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!data.length) {
        return (
            <div className="sf-state">
                <Scissors aria-hidden />
                <h2>Nenhum serviço cadastrado</h2>
                <p>Cadastre o que você oferece para os clientes poderem agendar.</p>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setParams({ action: 'quick-add' })}>
                    Criação rápida
                </button>
                <button type="button" className="sf-link" onClick={() => setParams({ action: 'add' })}>
                    Cadastro completo
                </button>
            </div>
        );
    }

    return (
        <div className="sf-page px-2">
            {isModalOpen && (
                <Modal
                    onClose={handleCloseOrderModal}
                    title="Ordenação"
                    subTitle="Arraste para definir a ordem em que os clientes veem os serviços."
                    desktopClassName="sm:!max-w-xl"
                >
                    <ServicosOrderConfig callback={reloadData} onCancel={handleCloseOrderModal} data={data} />
                </Modal>
            )}

            <div className="sf-toolbar-row">
                <div className="sf-segmented" role="group" aria-label="Situação">
                    <button type="button" aria-pressed={statusFilter === 'active'} onClick={() => setStatusFilter('active')}>
                        Ativos
                        <span className="sf-segmented-count">{activeCount}</span>
                    </button>
                    <button type="button" aria-pressed={statusFilter === 'inactive'} onClick={() => setStatusFilter('inactive')}>
                        Desativados
                        <span className="sf-segmented-count">{inactiveCount}</span>
                    </button>
                </div>
                <label className="sf-search">
                    <Search aria-hidden />
                    <input
                        type="search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar serviço"
                        aria-label="Buscar por nome ou descrição"
                    />
                </label>
            </div>

            {lista.length === 0 ? (
                <div className="sf-state">
                    <Search aria-hidden />
                    <h2>{q ? 'Nenhum serviço encontrado' : statusFilter === 'active' ? 'Nenhum serviço ativo' : 'Nenhum serviço desativado'}</h2>
                </div>
            ) : (
                <ul className="sf-group">
                    {lista.map((row: any) => {
                        const rulesCount = (row.priceRules || []).filter((rule: any) => rule?.active !== false).length;

                        return (
                            <li key={row._id}>
                                <div className="sf-row !p-0">
                                    <button
                                        type="button"
                                        className="sf-row min-w-0 flex-1 !pr-2"
                                        onClick={() => setParams({ action: 'edit', id: row._id })}
                                    >
                                        <span className="sf-row-body">
                                            <span className="sf-row-title">{row.name}</span>
                                            <span className="sf-row-sub">
                                                {formatDuration(Number(row.duration || 0))}
                                                {!!row.description && ` · ${row.description}`}
                                            </span>
                                            {(row.allowClientScheduling === false || row.requiresSubscription || rulesCount > 0) && (
                                                <span className="sf-tags">
                                                    {row.allowClientScheduling === false && (
                                                        <span className="sf-tag" data-tone="gray">
                                                            Só pela equipe
                                                        </span>
                                                    )}
                                                    {row.requiresSubscription && (
                                                        <span className="sf-tag" data-tone="blue">
                                                            Exclusivo de assinatura
                                                        </span>
                                                    )}
                                                    {rulesCount > 0 && (
                                                        <span className="sf-tag" data-tone="gray">
                                                            {rulesCount} {rulesCount === 1 ? 'preço especial' : 'preços especiais'}
                                                        </span>
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                        <span className="sf-row-end">{formatBRL(Number(row.price) || 0)}</span>
                                        <ChevronRight className="sf-chevron" aria-hidden />
                                    </button>
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-success toggle-sm mr-3.5 shrink-0"
                                        checked={row.status === STATUS_ACTIVE}
                                        onChange={() => toggleStatus(row)}
                                        disabled={togglingId === row._id}
                                        aria-label={`${row.name} ativo`}
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
